/* global React, Card, Button, Badge, Icon, EmptyState, ConfirmModal, toast, t, PlummeI18n */

// Samlet dagsoppgjørsflate. Oppgjør fra kassesystemer, bookingsystemer,
// utleieplattformer og nettbutikker samles her i stedet for i ett kort per
// leverandør inne i innstillinger.
const DAGSOPPGJOR_STATUS = {
  ready: { variant: 'success', label: 'dailySettlement.status.ready' },
  review: { variant: 'warning', label: 'dailySettlement.status.review' },
  approved: { variant: 'neutral', label: 'dailySettlement.status.approved' },
  dismissed: { variant: 'neutral', label: 'dailySettlement.status.dismissed' },
};
const DAGSOPPGJOR_FILTERS = ['open', 'ready', 'review', 'done'];

function DagsoppgjorAttention({ entries, onOpenSettings }) {
  if (!entries.length) return null;
  return <Card style={{ marginBottom: 18 }}>
    <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
      <Icon name="alert-triangle" size={20} />
      <div className="col gap-8" style={{ minWidth: 0, flex: 1 }}>
        <b className="t-sm">{t('dailySettlement.attentionTitle')}</b>
        {entries.map((entry) => <div key={entry.provider} className="t-sm muted" style={{ fontWeight: 600 }}>
          {entry.neverDelivered
            ? t('dailySettlement.attentionNever', { provider: entry.provider })
            : t('dailySettlement.attentionStale', { provider: entry.provider, days: entry.daysSinceLast, date: entry.lastDelivery })}
        </div>)}
      </div>
      <Button size="sm" variant="secondary" onClick={onOpenSettings}>{t('dailySettlement.openSettings')}</Button>
    </div>
  </Card>;
}

function Dagsoppgjor({ setRoute }) {
  const [payload, setPayload] = React.useState({ batches: [], dashboard: {}, attention: [] });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('open');
  const [approving, setApproving] = React.useState(null);
  const [reviewing, setReviewing] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!window.PlummeBackend?.listCommerceSettlements) { setLoading(false); return; }
    setLoading(true);
    try { setPayload(await window.PlummeBackend.listCommerceSettlements()); }
    catch (error) { toast(error?.message || t('dailySettlement.loadError'), 'error'); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { void load(); }, [load]);

  const approve = async () => {
    if (!approving) return;
    setBusy(true);
    try {
      await window.PlummeBackend.approveCommerceBatch(approving.provider, approving.id);
      setApproving(null);
      await load();
      toast(t('set.integr.commerceApproved'), 'success');
    } catch (error) { toast(error?.message || t('set.integr.commerceApproveError'), 'error'); }
    finally { setBusy(false); }
  };

  const batches = Array.isArray(payload?.batches) ? payload.batches : [];
  const dashboard = payload?.dashboard || {};
  const visible = batches.filter((batch) => {
    if (filter === 'open') return batch.status === 'ready' || batch.status === 'review';
    if (filter === 'done') return batch.status === 'approved' || batch.status === 'dismissed';
    return batch.status === filter;
  });
  const providerName = (provider) => window.PARTNER_SETTLEMENT_PROVIDERS?.[provider]?.name
    || (provider === 'shopify' ? 'Shopify' : provider === 'woocommerce' ? 'WooCommerce' : provider);

  return <div>
    <div className="row wrap gap-14" style={{ margin: '2px 2px 22px', alignItems: 'center' }}>
      <div>
        <div className="t-h1">{t('dailySettlement.title')}</div>
        <div className="t-sm muted" style={{ fontWeight: 600, marginTop: 4 }}>{t('dailySettlement.sub')}</div>
      </div>
    </div>

    <DagsoppgjorAttention entries={Array.isArray(payload?.attention) ? payload.attention : []} onOpenSettings={() => setRoute('innstillinger')} />

    <Card>
      <div className="row between wrap gap-12" style={{ marginBottom: 16, alignItems: 'center' }}>
        <div className="row gap-7">
          {DAGSOPPGJOR_FILTERS.map((key) => <button key={key} className={'seg-btn' + (filter === key ? ' on' : '')} onClick={() => setFilter(key)}>{t('dailySettlement.filter.' + key)}</button>)}
        </div>
        <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
          <Badge variant={dashboard.ready ? 'success' : 'neutral'}>{t('set.integr.commerceReady', { count: dashboard.ready || 0 })}</Badge>
          <Badge variant={dashboard.review ? 'warning' : 'neutral'}>{t('set.integr.commerceReview', { count: dashboard.review || 0 })}</Badge>
          <Button size="sm" variant="secondary" icon="refresh" disabled={loading} onClick={load}>{t('dailySettlement.refresh')}</Button>
        </div>
      </div>

      {loading ? <div className="t-sm muted">{t('set.integr.commerceLoading')}</div>
        : visible.length === 0 ? <EmptyState title={t('dailySettlement.emptyTitle')} text={t('dailySettlement.emptyText')}
          action={<Button variant="secondary" onClick={() => setRoute('innstillinger')}>{t('dailySettlement.openSettings')}</Button>} />
          : <div className="col gap-10">
            {visible.map((batch) => {
              const status = DAGSOPPGJOR_STATUS[batch.status] || DAGSOPPGJOR_STATUS.ready;
              return <div key={batch.id} style={{ padding: 14, border: '1px solid var(--border)', borderRadius: 'var(--r-ms)' }}>
                <div className="row between wrap gap-10" style={{ alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="row gap-8" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <b className="t-sm">{providerName(batch.provider)}</b>
                      <span className="t-sm muted" style={{ fontWeight: 600 }}>{batch.date}</span>
                      <Badge variant={status.variant}>{t(status.label)}</Badge>
                    </div>
                    <div className="t-xs muted" style={{ marginTop: 4 }}>
                      {t('set.integr.commerceBatchSummary', { sales: batch.saleCount || 0, refunds: batch.refundCount || 0, amount: PlummeI18n.money(batch.gross || 0, { decimals: 2 }) })}
                      {batch.paymentGateway ? ' · ' + batch.paymentGateway : ''}
                    </div>
                    {Array.isArray(batch.blockingRiskFlags) && batch.blockingRiskFlags.length > 0 &&
                      <div className="t-xs" style={{ color: 'var(--warning-ink)', marginTop: 6 }}>
                        <Icon name="alert-triangle" size={13} /> {t('set.integr.commerceRisks', { risks: batch.blockingRiskFlags.join(', ') })}
                      </div>}
                  </div>
                  <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                    {batch.status === 'ready' && <Button size="sm" onClick={() => setApproving(batch)} data-help={t('helpTopics.dailySettlement.body')} data-help-title={t('helpTopics.dailySettlement.title')}>{t('set.integr.commerceApprove')}</Button>}
                    {batch.status === 'review' && <Button size="sm" variant="secondary" icon="alert-triangle" onClick={() => setReviewing(batch)} data-help={t('helpTopics.dailySettlement.body')} data-help-title={t('helpTopics.dailySettlement.title')}>{t('set.integr.commerceResolve')}</Button>}
                  </div>
                </div>
              </div>;
            })}
          </div>}
    </Card>

    {window.SettingsCommerceBatchReview && <window.SettingsCommerceBatchReview provider={reviewing?.provider} batch={reviewing} open={!!reviewing}
      onClose={() => setReviewing(null)} onDone={load} />}
    <ConfirmModal open={!!approving} onClose={() => busy ? undefined : setApproving(null)} confirmIcon="check"
      title={t('set.integr.commerceApproveTitle')}
      message={approving ? t('set.integr.commerceApproveHelp', { date: approving.date, amount: PlummeI18n.money(approving.gross || 0, { decimals: 2 }) }) : ''}
      confirmLabel={t('set.integr.commerceApprove')} busy={busy} onConfirm={approve} />
  </div>;
}

// Påminnelsen på forsiden. Henter selv, siden oppgjørene ligger i egne
// Firestore-samlinger og ikke i app-state.
function DagsoppgjorHomeCard({ setRoute }) {
  const [payload, setPayload] = React.useState(null);

  React.useEffect(() => {
    let active = true;
    if (!window.PlummeBackend?.listCommerceSettlements) return undefined;
    window.PlummeBackend.listCommerceSettlements()
      .then((value) => { if (active) setPayload(value); })
      .catch(() => { if (active) setPayload(null); });
    return () => { active = false; };
  }, []);

  const dashboard = payload?.dashboard || {};
  const attention = Array.isArray(payload?.attention) ? payload.attention : [];
  const waiting = (dashboard.ready || 0) + (dashboard.review || 0);
  if (!payload || (!waiting && !attention.length)) return null;

  return <Card style={{ marginBottom: 18 }}>
    <div className="row between wrap gap-12" style={{ alignItems: 'center' }}>
      <div className="row gap-10" style={{ alignItems: 'flex-start', minWidth: 0 }}>
        <Icon name="receipt" size={20} />
        <div className="col gap-4" style={{ minWidth: 0 }}>
          <b className="t-sm">{t('dailySettlement.title')}</b>
          {waiting > 0 && <span className="t-sm muted" style={{ fontWeight: 600 }}>
            {t('dailySettlement.homeWaiting', { ready: dashboard.ready || 0, review: dashboard.review || 0 })}
          </span>}
          {attention.map((entry) => <span key={entry.provider} className="t-sm" style={{ fontWeight: 600, color: 'var(--warning-ink)' }}>
            {entry.neverDelivered
              ? t('dailySettlement.attentionNever', { provider: entry.provider })
              : t('dailySettlement.attentionStale', { provider: entry.provider, days: entry.daysSinceLast, date: entry.lastDelivery })}
          </span>)}
        </div>
      </div>
      <Button size="sm" onClick={() => setRoute('dagsoppgjor')}>{t('dailySettlement.homeAction')}</Button>
    </div>
  </Card>;
}

Object.assign(window, { Dagsoppgjor, DagsoppgjorHomeCard });
